import { Injectable } from '@angular/core';

import { BehaviorSubject, finalize, map, Observable, switchMap, take, tap } from 'rxjs';

import { Log } from 'src/app/models/log';
import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { LogQueries } from 'src/app/graphql/queries/log.queries';

/**
 * Provides a service that fetches logs from logger api
 */
@Injectable()
export class LogService {
	private loadingSubject = new BehaviorSubject<boolean>(false);

	public isLoadingDeliveries$ = this.loadingSubject.asObservable();

	/**
	 * Searches logs based on session id
	 * @param sessionId
	 * @param logGroups
	 * @param searchFilter
	 * @returns
	 */
	public logsBySessionId$(sessionId, logGroups, searchFilter?): Observable<Log[] | undefined> {
		return this.apolloClientService.loggerApiClient.pipe(
			tap(() => this.loadingSubject.next(true)),
			take(1),
			switchMap(client =>
				client.query({
					query: LogQueries.logsBySessionId,
					variables: { sessionId: sessionId, logGroups: logGroups, searchFilter: searchFilter ?? {} },
					fetchPolicy: 'no-cache',
				}),
			),
			map((res: any) => {
				let idCounter = 1;
				if (res.data.logsBySessionId) {
					return res.data.logsBySessionId.map(log => {
						if (!log) {
							return null;
						}

						return {
							Id: (idCounter++).toString(),
							LogGroupName: log.logGroupName,
							LogStreamLink: log.logStreamLink,
							Message: (() => {
								try {
									return JSON.parse(log.message);
								} catch {
									return log.message;
								}
							})(),
							Timestamp: log.timestamp ? new Date(log.timestamp) : null,
						} as Log;
					});
				}
				return null;
			}),
			finalize(() => this.loadingSubject.next(false)),
		);
	}

	/**
	 * Searches for logs based on x-correlation-id which is the unique identifier for each request
	 * @param correlationId
	 * @param logGroups
	 * @param searchFilter
	 * @returns
	 */
	public logsByCorrelationId$(correlationId, logGroups, searchFilter?): Observable<Log[] | undefined> {
		return this.apolloClientService.loggerApiClient.pipe(
			tap(() => this.loadingSubject.next(true)),
			take(1),
			switchMap(client =>
				client.query({
					query: LogQueries.logsByCorrelationId,
					variables: { correlationId: correlationId, logGroups: logGroups, searchFilter: searchFilter ?? {} },
					fetchPolicy: 'no-cache',
				}),
			),
			map((res: any) => {
				let idCounter = 1;
				if (res.data.logsByCorrelationId) {
					return res.data.logsByCorrelationId.map(log => {
						if (!log) {
							return null;
						}

						return {
							Id: (idCounter++).toString(),
							LogGroupName: log.logGroupName,
							LogStreamLink: log.logStreamLink,
							Message: (() => {
								try {
									return JSON.parse(log.message);
								} catch {
									return log.message;
								}
							})(),
							Timestamp: log.timestamp ? new Date(log.timestamp) : null,
						} as Log;
					});
				}
				return null;
			}),
			finalize(() => this.loadingSubject.next(false)),
		);
	}

	public logGroups$ = this.apolloClientService.loggerApiClient.pipe(
		take(1),
		switchMap(client =>
			client.query<{ logGroups: string[] }>({
				query: LogQueries.logGroups,
			}),
		),
		map(response => {
			return response.data.logGroups;
		}),
	);

	constructor(private apolloClientService: ApolloClientService) {}
}
